import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { apiJson, ApiError } from '../lib/api'
import { canOpen, hasPacking, useAccess } from '../lib/access'
import { offsetText, zoneOptions, ZoneNow } from '../lib/timezones'
import { dmy } from './wages/format'
import { BoxDraft, BoxType, WEIGHTS, band, boxAdvice, boxBody, boxErrors, grams, toBoxDraft, tolerance, usesText, valuesOf } from './settings/boxTypes'
import { useLayer } from '../components/layers'
import { SkeletonRows, PendingButton, useSlow, inertClass } from '../components/pending'
import { fmtStamp } from './timesheets/time'
import { LargeBatchThreshold } from './settings/LargeBatchThreshold'
import { RuleLineList } from './productActivity/RuleLines'
import { STATE_TEXT, TONE, type RuleLine } from './productActivity/model'
import { PageHeader, PageBody, TabBar, headerStyles } from '../components/PageHeader'
import pa from './ProductActivityPage.module.css'
import pj from './PickingJobsPage.module.css'
import o from '../components/Overlay.module.css'
import styles from './GeneralSettingsPage.module.css'
import { openableRow } from '../lib/openableRow'
import { DateField } from '../components/DateField'

// Configuration → General settings (handoff §7n.16): Product & modules,
// Packing (box types & weights, §7n.14) and Processing rules (§7h, §9.1,
// read-only) and Client Setup. Client Setup is Dataphyll SYSADMIN's tab: it
// is absent, not disabled, for anyone else, and a ?tab=client they are sent
// falls back to Product & modules. The role is the membership's, from
// /api/me; the API refuses a write from any other role regardless.
//
// The product and the Stock & Dispatch licence (D2, Kerrin 2026-10-01) are
// Dataphyll's: the Product & modules tab shows them read-only, and only
// SYSADMIN changes them, in Client Setup. The Packing tab follows the product
// (PackRight), never the licence.

type TabKey = 'product' | 'packing' | 'rules' | 'client' | 'requests'
const TABS: [TabKey, string][] = [
  ['product', 'Product & modules'],
  ['packing', 'Packing'],
  ['rules', 'Processing rules'],
]

export const GeneralSettingsPage: React.FC = () => {
  const [params, setParams] = useSearchParams()
  const { currentWorkspace } = useWorkspace()
  const access = useAccess()
  const sysadmin = currentWorkspace?.role === 'SYSADMIN'
  const own = hasPacking(access) ? TABS : TABS.filter(([k]) => k !== 'packing')
  const tabs: [TabKey, string][] = sysadmin ? [...own, ['requests', 'Rule requests'], ['client', 'Client Setup']] : own
  const asked = params.get('tab') as TabKey | null
  const tab: TabKey = tabs.some(([k]) => k === asked) ? asked! : 'product'

  return (
    <>
      <PageHeader
        title="General settings"
        tabs={
          <TabBar label="General settings">
            {tabs.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={k === 'client' || k === 'requests' ? (tab === k ? styles.clientTabOn : styles.clientTab) : tab === k ? headerStyles.tabOn : headerStyles.tab}
                onClick={() => setParams({ tab: k }, { replace: true })}
              >
                {label}
              </button>
            ))}
          </TabBar>
        }
      />
      <PageBody>
        {tab === 'product' && <ProductTab />}
        {tab === 'packing' && <PackingTab />}
        {tab === 'rules' && <RulesTab />}
        {tab === 'requests' && <RuleRequestsTab />}
        {tab === 'client' && <ClientSetupTab />}
      </PageBody>
    </>
  )
}

// The two products and what each includes (h 5856-5859).
const PRODUCT_TEXT: Record<'grow' | 'packright', { label: string; who: string; includes: string }> = {
  grow: {
    label: 'Dataphyll Grow',
    who: 'Every grower — cherries, blueberries, pollen and the rest',
    includes:
      'Hours and harvest recorded in the field then reconciled; wage calculation, minimum-rate checks and payroll export; picking jobs, orchard structure, groups and teams; product activity, batches, quality control and reports.',
  },
  packright: {
    label: 'Dataphyll PackRight',
    who: 'Table grapes, packing in the field',
    includes:
      "Everything in Grow, plus PackRight devices weighing each box against its box type's net weight and thresholds and capturing the pack; box types maintained in Control; eligible for the Stock & Dispatch module.",
  },
}
const LICENCE_TEXT: Record<string, string> = { paid: 'paid', included: 'included in the subscription' }

// Product & modules (h 5852-5869). The product is one commercial fact,
// Dataphyll-set and read-only here. The module list is PackRight's alone: on
// Grow, Stock & Dispatch is absent everywhere rather than listed as "not
// available" (D2 item 2 over h 5867; docs/DECISIONS.md).
function ProductTab() {
  const { user } = useAuth()
  const access = useAccess()
  const [data, setData] = useState<TenantSettings | null>(null)
  const [error, setError] = useState('')
  const uid = user?.uid

  useEffect(() => {
    if (!user) return
    let live = true
    apiJson<TenantSettings>(user, '/api/settings/tenant')
      .then((t) => live && setData(t))
      .catch((e: any) => live && setError(e?.message || 'Could not load the product'))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  if (!data)
    return error ? (
      <div className={styles.error} role="alert">
        <p>{error}</p>
      </div>
    ) : (
      <div className={styles.loading}>Loading the product...</div>
    )

  const product = data.product ? PRODUCT_TEXT[data.product] : null
  const sd = data.modules?.stock_dispatch
  const hasPkng = hasPacking(access)
  return (
    <div className={styles.productTab}>
      <div className={styles.panelCard} data-testid="product-block">
        <div className={styles.cardHeader}>
          <span className={styles.cardHeaderLabel}>Product</span>
          <span className={styles.cardHeaderChip}>Dataphyll sets this</span>
        </div>
        <div className={styles.cardBody}>
          {product ? (
            <>
              <h2 className={styles.productName}>{product.label}</h2>
              <p className={styles.productWho}>{product.who}</p>
              <ul className={styles.productBullets}>
                {product.includes.split(';').map((item, idx) => {
                  const trimmed = item.trim().replace(/\.$/, '')
                  return <li key={idx}>{trimmed.charAt(0).toUpperCase() + trimmed.slice(1)}</li>
                })}
              </ul>
            </>
          ) : (
            <p className={styles.productName}>Dataphyll has not set this workspace's product yet</p>
          )}
          <p className={styles.productNote}>
            Set by Dataphyll, not here: the product is what this workspace has bought, and it decides which screens exist. To change it, email{' '}
            <a href="mailto:support@dataphyll.com">support@dataphyll.com</a>.
          </p>
        </div>
      </div>
      {hasPkng && (
        <div className={styles.panelCard} data-testid="packing-block">
          <div className={styles.cardHeader}>
            <span className={styles.cardHeaderLabel}>Packing</span>
          </div>
          <div className={styles.moduleRow}>
            <div className={styles.moduleText}>
              <span className={styles.moduleName}>Box types & weights</span>
              <p className={styles.moduleDescription}>Configure the box types for picking jobs and their net weights for Packright.</p>
            </div>
            <Link to="?tab=packing" className={styles.modulePillBtn}>
              Box types
            </Link>
          </div>
        </div>
      )}
      {data.product === 'packright' && sd && (
        <div className={styles.panelCard} data-testid="modules-block">
          <div className={styles.cardHeader}>
            <span className={styles.cardHeaderLabel}>Optional modules</span>
          </div>
          <div className={styles.moduleRow} data-testid="module-stock_dispatch">
            <div className={styles.moduleText}>
              <div className={styles.moduleNameChip}>
                <span className={styles.moduleName}>Stock & Dispatch</span>
                <span className={sd.state === 'licensed' ? styles.moduleOn : styles.moduleOff}>
                  {sd.state === 'licensed' ? `Licensed${sd.licence ? `, ${LICENCE_TEXT[sd.licence] || sd.licence}` : ''}` : 'Available, not licensed'}
                </span>
              </div>
              <p className={styles.moduleDescription}>Turns received fruit into pallets, customer orders and verified truck loads, with labels, paperwork and draft Xero invoices for what left.</p>
            </div>
            {sd.state === 'licensed' && canOpen(access, 'config_stock_dispatch') && (
              <Link className={styles.modulePillBtn} to="/stock-dispatch/settings">
                Module settings
              </Link>
            )}
            {sd.state === 'not_licensed' && access?.module_preview && (
              <Link className={styles.modulePillBtn} to="/stock-dispatch/preview">
                See what it does
              </Link>
            )}
          </div>
          <p className={styles.moduleFooter}>Modules are licensed separately, and a change applies to new work in each module. Email support@dataphyll.com to add or change a licence.</p>
        </div>
      )}
    </div>
  )
}

// ── Packing: box types & weights (§7n.14) ────────────────────────────────

function PackingTab() {
  const { user } = useAuth()
  const [types, setTypes] = useState<BoxType[] | null>(null)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState<BoxDraft | null>(null)
  const [toast, setToast] = useState('')
  // The page state that the rows on screen belong to: a first load or a new
  // period draws skeleton rows, a reload of the same period after a correction
  // does not (h 3994-4002).
  const [shown, setShown] = useState<string | null>(null)
  const loading = shown !== 'packing'
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const seq = useRef(0)

  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const n = ++seq.current
    setError('')
    try {
      const r = await apiJson<BoxType[]>(u, '/api/settings/box-types')
      if (n !== seq.current) return
      setTypes(r)
    } catch (e: any) {
      if (n !== seq.current) return
      setError(e?.message || 'Could not load the box types')
      setTypes((x) => x || [])
    } finally {
      if (n === seq.current) setShown('packing')
    }
  }, [uid])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3600)
    return () => clearTimeout(t)
  }, [toast])

  const saved = draft?.id ? types?.find((t) => t.id === draft.id) || null : null
  const done = (message: string) => {
    setDraft(null)
    setToast(message)
    load()
  }

  return (
    <>
      <p className={styles.intro}>
        A box type is what a picking job packs into: how many make a pallet, and the weights Packright accepts. A change applies to the next box weighed and
        the next job saved; nothing already weighed or saved is recalculated.
      </p>
      {error && (
        <div className={styles.error} role="alert">
          <p>{error}</p>
          <button type="button" className={pj.archiveBtn} onClick={load}>
            Retry
          </button>
        </div>
      )}
      <div className={styles.toolbar}>
        <button type="button" className={pa.primaryBtn} onClick={() => setDraft(toBoxDraft(null))} disabled={loading}>
          + Add box type
        </button>
      </div>
      <div className={pa.card}>
        <div className={styles.table} role="table" aria-label="Box types">
          <div className={styles.headRow} role="row">
            {['Box type', 'Per pallet', 'Net', 'Min', 'Max', 'Tolerance', 'Used by', 'Status'].map((h) => (
              <span key={h} role="columnheader">
                {h}
              </span>
            ))}
          </div>
          {loading ? (
            <SkeletonRows rowClass={styles.row} cols={8} label="Loading box types" />
          ) : types!.length === 0 ? (
            <div className={pa.empty}>No box types yet. Add one to choose it on a picking job.</div>
          ) : (
            types!.map((t) => (
              <div key={t.id} role="row" data-testid="box-type-row" className={t.active ? styles.row : styles.rowOff} {...openableRow(() => setDraft(toBoxDraft(t)))}>
                <span className={styles.name}>{t.name}</span>
                <span className={styles.cell}>{t.boxes_per_pallet}</span>
                <span className={t.net_weight_g == null ? styles.cellNone : styles.cell}>{grams(t.net_weight_g)}</span>
                <span className={t.min_weight_g == null ? styles.cellNone : styles.cell}>{grams(t.min_weight_g)}</span>
                <span className={t.max_weight_g == null ? styles.cellNone : styles.cell}>{grams(t.max_weight_g)}</span>
                <span className={styles.cell}>{band(t)}</span>
                <span className={styles.cell}>{usesText(t.uses)}</span>
                <span className={t.active ? styles.status : styles.statusOff}>{t.active ? 'Active' : 'Inactive'}</span>
              </div>
            ))
          )}
        </div>
        {!loading && (
          <div className={styles.foot}>
            {types!.filter((t) => t.active).length} active of {types!.length}
          </div>
        )}
      </div>

      {draft && <BoxTypeDrawer draft={draft} setDraft={setDraft} saved={saved} onDone={done} />}

      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}

interface DrawerProps {
  draft: BoxDraft
  setDraft: (d: BoxDraft | null) => void
  saved: BoxType | null
  onDone: (message: string) => void
}

function BoxTypeDrawer({ draft: d, setDraft, saved, onDone }: DrawerProps) {
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState('')
  const [removing, setRemoving] = useState(false)
  const isNew = !saved
  const patch = (p: Partial<BoxDraft>) => setDraft({ ...d, ...p })

  // The drawer is a draft until Save, and a layer (h 425-458): Escape, the
  // scrim and the × close it, asking first once anything has been changed.
  const panelEl = useRef<HTMLDivElement>(null)
  const start = useRef(d)
  const dirty = !busy && JSON.stringify(d) !== JSON.stringify(start.current)
  // Nothing closes it while its write is out (h 3984-3992).
  const { requestClose: close, confirm } = useLayer(true, () => setDraft(null), { dirty, el: panelEl, modal: true, focusFirst: true, busy })
  // The remove question is a layer over it: Escape means Keep it.
  useLayer(removing, () => setRemoving(false), { busy })
  const slow = useSlow(busy)

  const v = valuesOf(d)
  const errors = boxErrors(v)
  const advice = errors.length ? [] : boxAdvice(v)
  const body = boxBody(d, saved)
  const unchanged = !!saved && !Object.keys(body).length
  const nameLocked = !!saved?.rename_blocked
  const title = isNew ? 'New box type' : 'Edit box type'

  const save = async () => {
    if (busy || errors.length) return
    if (unchanged) {
      setDraft(null)
      return
    }
    setBusy(true)
    setServerError('')
    try {
      const t = await apiJson<BoxType>(user, saved ? `/api/settings/box-types/${saved.id}` : '/api/settings/box-types', {
        method: saved ? 'PUT' : 'POST',
        body,
      })
      onDone(isNew ? `${t.name} added` : `${t.name} saved`)
    } catch (e: any) {
      setServerError(e?.message || 'The box type was not saved')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!saved || busy) return
    setBusy(true)
    setServerError('')
    try {
      await apiJson(user, `/api/settings/box-types/${saved.id}`, { method: 'DELETE' })
      onDone(`${saved.name} removed`)
    } catch (e: any) {
      setServerError(e?.message || 'The box type was not removed')
      setRemoving(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && close()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${styles.boxDrawer}`} role="dialog" aria-label={title}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>{title}</h2>
            <p className={o.drawerSub}>{saved ? `${saved.name} · ${usesText(saved.uses)}` : 'Packing · Box types & weights'}</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={close} disabled={busy}>
            <CloseIcon />
          </button>
        </div>

        <div className={`${o.drawerBody} ${styles.boxBody} ${inertClass(busy, slow)}`}>
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="bt-name">
              Box / pack type name <span className={o.req}>required</span>
            </label>
            <input
              id="bt-name"
              className={d.name.trim() ? o.input : `${o.input} ${o.inputBad}`}
              value={d.name}
              disabled={nameLocked}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="e.g. 9 kg bunch bag"
            />
            {nameLocked && (
              <p className={styles.lockNote}>
                Stock groups or order lines refer to this name, so it cannot change. Add a new box type and deactivate this one instead.
              </p>
            )}
          </div>

          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="bt-bpp">
              Default boxes per pallet <span className={o.req}>required</span>
            </label>
            <input id="bt-bpp" className={`${o.input} ${styles.numInput} ${styles.bppInput}`} inputMode="numeric" value={d.boxes_per_pallet} onChange={(e) => patch({ boxes_per_pallet: e.target.value })} />
            <span className={o.fieldHint}>Copied onto a picking job when the job is saved with this type; later changes here do not recalculate it.</span>
          </div>

          <div className={o.section}>
            <div className={o.label}>Weights: grams</div>
            <div className={styles.weights}>
              {WEIGHTS.map(([k, label]) => (
                <div key={k} className={o.field}>
                  <label className={o.fieldLabel} htmlFor={`bt-${k}`}>
                    {label}
                  </label>
                  <input id={`bt-${k}`} className={`${o.input} ${styles.numInput}`} inputMode="numeric" value={d[k]} onChange={(e) => patch({ [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <div className={styles.tolerance} data-testid="bt-tolerance">
              {tolerance(v)}
            </div>
            <span className={o.fieldHint}>
              The net weight is the labelled weight the box must at least contain, so the accepted window sits at or above it: net weight ≤ minimum ≤
              maximum. Packright reads these when it weighs a box. Leave them empty while they are not decided.
            </span>
          </div>

          <div className={o.toggleRow}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Active</div>
              <div className={o.toggleSub}>
                {d.active ? 'Offered when a picking job or order line chooses a box type.' : 'Kept on every record that already uses it, never offered on a new one.'}
              </div>
            </div>
            <button type="button" role="switch" aria-checked={d.active} aria-label="Active" className={o.switch} onClick={() => patch({ active: !d.active })} />
          </div>

          {errors.length > 0 && (
            <ul className={styles.errors} role="alert" aria-label="Problems">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          {advice.map((a) => (
            <p key={a} className={styles.advice} data-testid="bt-advice">
              {a}
            </p>
          ))}
          {serverError && (
            <p className={o.danger} role="alert">
              {serverError}
            </p>
          )}
        </div>

        <div className={`${o.drawerFoot} ${styles.boxFoot}`}>
          {removing ? (
            <>
              <span className={o.footNote}>Remove {saved!.name}? This cannot be undone.</span>
              <button type="button" className={o.ghost} disabled={busy} onClick={() => setRemoving(false)}>
                Keep it
              </button>
              <PendingButton className={o.dangerBtn} busy={busy} pending="Removing…" onClick={remove}>
                Remove box type
              </PendingButton>
            </>
          ) : (
            <>
              <PendingButton className={o.primary} disabled={!!errors.length} busy={busy} pending={isNew ? 'Adding…' : 'Saving…'} onClick={save}>
                {isNew ? 'Add box type' : 'Save changes'}
              </PendingButton>
              <button type="button" className={o.ghost} disabled={busy} onClick={close}>
                Cancel
              </button>
              {saved && !saved.removable && <span className={`${o.fieldHint} ${styles.removeNote}`}>In use: deactivate instead of removing.</span>}
              {saved && (
                <button
                  type="button"
                  className={styles.removeBtn}
                  disabled={busy || !saved.removable}
                  title={saved.removable ? undefined : 'In use: deactivate instead of removing.'}
                  onClick={() => setRemoving(true)}
                >
                  Remove
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {confirm}
    </div>
  )
}

// ── Processing rules (§7h, §9.1): read-only, Dataphyll-set ───────────────

interface Rules {
  reuse_minutes: number
  competing_preassign: string
  preassign_validity: string
  cycle_time_source: string
  repeated_pick: string
  late_data: string
  // 28g (097): the two QC rules and the automatic lock age (none: null).
  qc_association?: string
  qc_effective?: string
  day_lock_after_days?: number | null
}

const VALUE_TEXT: Record<string, string> = {
  first_claim_wins: 'First claim wins',
  midnight_following_day: 'Until midnight the following day',
  winning_preassign: 'Winning PREASSIGN',
  pick: 'PICK',
  latest_supersedes: 'Latest supersedes earlier',
  stage_for_approval: 'Stage for approval',
  either_side: 'Either side of receipt',
  before_receipt: 'Before receipt',
  after_receipt: 'After receipt',
  after_day_close: 'After day close',
}

// The eight rules of §9.1 in its order, with its "if wrong" line.
// `req` is the key a change request names it by (routes/settings.js RULE_KEYS).
const RULES: { key: keyof Rules; req: string; name: string; ifWrong: string }[] = [
  { key: 'reuse_minutes', req: 'reuse_minutes', name: 'Minimum item reuse time', ifWrong: 'Two real picks merge into one, or one weigh-twice correction becomes two cycles' },
  { key: 'competing_preassign', req: 'competing_preassign', name: 'Competing PREASSIGN rule', ifWrong: 'The wrong picker is credited when two claim one bucket' },
  { key: 'preassign_validity', req: 'preassign_validity', name: 'PREASSIGN validity', ifWrong: 'Next-morning receipts lose their claim and land unassigned' },
  { key: 'cycle_time_source', req: 'cycle_time_source', name: 'Product cycle time source', ifWrong: 'Volume and production land on the wrong operational day' },
  { key: 'repeated_pick', req: 'repeated_pick', name: 'Repeated PICK inside reuse period', ifWrong: 'Double-counted weight' },
  { key: 'qc_association', req: 'qc_association', name: 'QC association window', ifWrong: 'A deduction penalises the wrong worker' },
  { key: 'qc_effective', req: 'qc_effective', name: 'QC deductions effective', ifWrong: 'Live-day figures move under people who are watching them' },
  { key: 'late_data', req: 'late_data', name: 'Late data handling', ifWrong: 'A closed, paid day silently changes' },
]

// h 2743: the day states restated. Each reads its line from the one the day
// banner and drawer use (STATE_TEXT, h 2640-2643), then what the state means
// for scans and edits, from the recalculation register (h 2509-2527).
const DAY_STATES: { tone: 'live' | 'closed' | 'locked'; then: string }[] = [
  { tone: 'live', then: 'Each scan is read as it arrives. A quality deduction is provisional until the day closes. Edits are allowed and audited.' },
  { tone: 'closed', then: 'Closing reads the day\'s quality scans one last time and applies their deductions. A scan that arrives after that is staged, with its impact shown, for an administrator to approve. Edits are still allowed and audited.' },
  { tone: 'locked', then: 'Edits and bulk edits are refused until an administrator unlocks the day, and the unlock is itself audited. Late data still stages.' },
]

interface Example {
  item: string
  events: { at: string; text: string }[]
  lines: RuleLine[]
}

// What Dataphyll answered (D9.5). A response records a decision; it does not
// change a rule. A workspace is told "Dataphyll", not who gave the answer.
interface Answer {
  seq: number
  outcome: 'done' | 'declined'
  note: string
  responded_by: string
  responded_at: string
}

interface ChangeRequest {
  id: string
  rule_keys: string[]
  requested_change: string
  reason: string | null
  requested_by: string
  requested_at: string
  // Absent on a request just sent: it is pending.
  status?: 'pending' | 'done' | 'declined'
  response?: Answer | null
  responses?: Answer[]
}

const STATUS_TEXT = { pending: 'Waiting for Dataphyll', done: 'Done', declined: 'Declined' } as const
const statusChip = (s: 'pending' | 'done' | 'declined') => (s === 'done' ? styles.badgeDone : s === 'declined' ? styles.badgeDeclined : '')

interface RulesData {
  rules: Rules
  defaults: boolean
  updated_at: string | null
  example?: Example
}

const ruleName = (req: string) => RULES.find((r) => r.req === req)?.name || req

const ruleValue = (rules: Rules, key: keyof Rules) => {
  const v = rules[key]
  if (key === 'reuse_minutes') return `${v} min`
  // The window's length is the reuse time (QCWIN-1), so it is said here.
  if (key === 'qc_association') return `${VALUE_TEXT[String(v || 'either_side')]}, within ${rules.reuse_minutes} min`
  if (v === undefined || v === null) return 'Not set'
  return VALUE_TEXT[String(v)] || String(v)
}

function RulesTab() {
  const { user } = useAuth()
  const [data, setData] = useState<RulesData | null>(null)
  const [requests, setRequests] = useState<ChangeRequest[] | null>(null)
  const [error, setError] = useState('')
  const [asking, setAsking] = useState(false)
  const [sent, setSent] = useState(false)
  const uid = user?.uid

  useEffect(() => {
    if (!user) return
    apiJson<RulesData>(user, '/api/settings/processing-rules')
      .then(setData)
      .catch((e: any) => setError(e?.message || 'Could not load the processing rules'))
    apiJson<ChangeRequest[]>(user, '/api/settings/processing-rules/change-requests')
      .then(setRequests)
      .catch(() => setRequests([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  if (error)
    return (
      <div className={styles.error} role="alert">
        <p>{error}</p>
      </div>
    )
  if (!data) return <div className={styles.loading}>Loading processing rules...</div>

  return (
    <>
      <p className={styles.intro}>
        How Control turns scans into product cycles. Dataphyll sets these, so a rule is never changed mid-harvest by accident.
      </p>
      <div className={pa.card}>
        <div className={styles.rules} role="table" aria-label="Processing rules">
          {RULES.map((r) => (
            <div key={r.name} className={styles.rule} role="row" data-testid="rule-row">
              <span className={styles.ruleName}>{r.name}</span>
              <span className={styles.ruleValue}>{ruleValue(data.rules, r.key)}</span>
              <span className={styles.ruleWhy}>If it were wrong: {r.ifWrong}</span>
            </div>
          ))}
        </div>
        <div className={styles.rulesFoot}>
          <span>{data.defaults ? "Dataphyll's defaults are in force." : 'Set by Dataphyll for this workspace.'}</span>
          <button type="button" className={pj.archiveBtn} onClick={() => setAsking(true)}>
            Request a change
          </button>
        </div>
        {sent && (
          <div className={styles.rulesSent} role="status">
            Sent to Dataphyll. The rules stay as they are until Dataphyll changes them.
          </div>
        )}
      </div>

      {requests && requests.length > 0 && (
        <section className={styles.rulesPanel} aria-label="Change requests">
          <h2>Requests sent</h2>
          {requests.some((q) => q.response) && (
            <p className={styles.ruleWhy} data-testid="rule-request-note">
              An answer records what Dataphyll decided. It does not change a rule by itself: the rules above are the ones in force now.
            </p>
          )}
          <ul className={styles.requestList}>
            {requests.map((q) => {
              const status = q.status || 'pending'
              const answers = [...(q.responses || [])].reverse()
              return (
                <li key={q.id} data-testid="rule-request">
                  <b>{q.rule_keys.map(ruleName).join(', ')}</b>
                  <span>{q.requested_change}</span>
                  {q.reason && <span className={styles.ruleWhy}>Why: {q.reason}</span>}
                  <span className={styles.ruleWhy}>
                    {q.requested_by} · {fmtStamp(q.requested_at)}
                  </span>
                  <span className={`${styles.badge} ${statusChip(status)}`} data-testid="rule-request-status">
                    {STATUS_TEXT[status]}
                  </span>
                  {answers.map((a, i) => (
                    <div key={a.seq} className={`${styles.answer} ${i > 0 ? styles.answerEarlier : ''}`} data-testid="rule-request-answer">
                      <span>
                        <b>{a.outcome === 'done' ? 'Done' : 'Declined'}</b>
                        {i > 0 && ' (replaced by a later answer)'}
                      </span>
                      <span>{a.note}</span>
                      <span className={styles.ruleWhy}>
                        {a.responded_by} · {fmtStamp(a.responded_at)}
                      </span>
                    </div>
                  ))}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {data.example && (
        <section className={styles.rulesPanel} aria-label="Worked example">
          <h2>Worked example</h2>
          <p className={styles.ruleWhy}>
            {data.example.item}, read by the rules above. The lines are the ones a cycle's Why this cycle shows, written the same way.
          </p>
          <ol className={styles.exampleEvents} aria-label="What was scanned">
            {data.example.events.map((e, i) => (
              <li key={i}>{e.text}</li>
            ))}
          </ol>
          <RuleLineList lines={data.example.lines} label="What Control decides" />
        </section>
      )}

      <section className={styles.rulesPanel} aria-label="Day states">
        <h2>Day states</h2>
        {DAY_STATES.map((s) => (
          <div key={s.tone} className={styles.dayState} data-testid="day-state">
            <span className={styles.dayStateName} style={{ background: TONE[s.tone].bg, borderColor: TONE[s.tone].line, color: TONE[s.tone].ink }}>
              {STATE_TEXT[s.tone].name}
            </span>
            <span>
              {STATE_TEXT[s.tone].line} {s.then}
            </span>
          </div>
        ))}
        {/* h 2774-2776: the lock by age, set by Dataphyll like the rules (DAYLOCK-1). */}
        <p className={styles.ruleWhy} data-testid="day-lock-age">
          {data.rules.day_lock_after_days
            ? `A closed day locks by itself ${data.rules.day_lock_after_days} day${data.rules.day_lock_after_days === 1 ? '' : 's'} after the day. A day an administrator unlocks is not locked again automatically.`
            : 'No automatic lock is set for this workspace, so a closed day stays closed until an administrator locks it.'}
        </p>
      </section>

      {asking && (
        <RequestChange
          onClose={() => setAsking(false)}
          onSent={(q) => {
            setAsking(false)
            setSent(true)
            setRequests((r) => [q, ...(r || [])])
          }}
        />
      )}
    </>
  )
}

// Request a change (h 2738-2739): a request to Dataphyll, recorded; it never
// changes a rule. A layer holding a draft asks before it is discarded.
function RequestChange({ onClose, onSent }: { onClose: () => void; onSent: (q: ChangeRequest) => void }) {
  const { user } = useAuth()
  const panelEl = useRef<HTMLDivElement>(null)
  const [rule, setRule] = useState('')
  const [change, setChange] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const dirty = !!(rule || change.trim() || reason.trim())
  const { requestClose, confirm } = useLayer(true, onClose, { dirty, el: panelEl, modal: true, focusFirst: true, busy })
  const ready = !!rule && !!change.trim()

  const send = async () => {
    if (!user || !ready) return
    setBusy(true)
    setErr('')
    try {
      const q = await apiJson<ChangeRequest>(user, '/api/settings/processing-rules/change-requests', {
        method: 'POST',
        body: { rule_keys: [rule], requested_change: change.trim(), reason: reason.trim() || null },
      })
      onSent(q)
    } catch (e: any) {
      setErr(e?.message || 'The request could not be sent')
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${styles.ruleReqModal}`} role="dialog" aria-label="Request a rule change">
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Request a rule change</h2>
            <p className={o.sub}>
              Rules are edited by Dataphyll so a change is never made mid-harvest by accident: a change reinterprets every product cycle from the day it
              takes effect. Tell us which rule and what your workflow actually does. Sending this asks Dataphyll; it changes nothing by itself.
            </p>
          </div>
        </div>
        <div className={`${o.body} ${styles.ruleReqBody}`}>
          <label className={o.field}>
            <span className={o.fieldLabel}>Rule</span>
            <select className={o.input} value={rule} onChange={(e) => setRule(e.target.value)}>
              <option value="">Pick a rule</option>
              {RULES.map((r) => (
                <option key={r.req} value={r.req}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <label className={o.field}>
            <span className={o.fieldLabel}>The change you need</span>
            <textarea className={o.input} rows={3} maxLength={2000} value={change} onChange={(e) => setChange(e.target.value)} placeholder="e.g. our packhouse round trip is closer to two hours than 90 minutes" />
          </label>
          <label className={o.field}>
            <span className={o.fieldLabel}>Why. Optional.</span>
            <textarea className={`${o.input} ${styles.shortArea}`} rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What happens on the ground that the rule gets wrong" />
          </label>
          {err && (
            <p className={o.danger} role="alert">
              {err}
            </p>
          )}
        </div>
        <div className={`${o.foot} ${styles.ruleReqFoot}`}>
          <PendingButton busy={busy} pending="Sending…" className={o.primary} onClick={send} disabled={!ready}>
            Send request
          </PendingButton>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
      {confirm}
    </div>
  )
}

// ── Rule requests (D9.5, Dataphyll SYSADMIN) ───────────────────────────────
//
// The queue of what workspaces asked Dataphyll to change in their processing
// rules, across every workspace this sign-in supports, each naming its
// subscription. A reviewer records Done or Declined with a note the workspace
// reads on its Requests sent list. Recording an answer changes no rule, replays
// no event and recalculates no wages; the rules shown beside the request are
// the ones recorded when it was sent and the ones in force now, so the reviewer
// can see whether anything has changed since.

interface QueueItem extends ChangeRequest {
  tenant_id: string
  tenant_name: string
  status: 'pending' | 'done' | 'declined'
  responses?: Answer[]
}
interface QueueDetail {
  request: QueueItem & { responses: Answer[] }
  rules_when_sent: Rules | null
  rules_now: Rules
}

function RuleRequestsTab() {
  const { user } = useAuth()
  const [filter, setFilter] = useState<'pending' | 'answered' | 'all'>('pending')
  const [items, setItems] = useState<QueueItem[] | null>(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState<{ tenant_id: string; id: string } | null>(null)
  const [detail, setDetail] = useState<QueueDetail | null>(null)
  const [outcome, setOutcome] = useState<'done' | 'declined' | ''>('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [formError, setFormError] = useState('')
  const uid = user?.uid

  const loadQueue = useCallback(() => {
    if (!user) return
    return apiJson<{ requests: QueueItem[] }>(user, `/api/support/rule-requests?status=${filter}`)
      .then((r) => {
        setItems(r.requests)
        setError('')
      })
      .catch((e: any) => setError(e?.message || 'Could not load the rule requests'))
  }, [user, filter])

  useEffect(() => {
    loadQueue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, filter])

  const view = async (q: { tenant_id: string; id: string }) => {
    if (!user) return
    setOpen(q)
    setDetail(null)
    setOutcome('')
    setNote('')
    setNotice('')
    setFormError('')
    try {
      setDetail(await apiJson<QueueDetail>(user, `/api/support/rule-requests/${q.tenant_id}/${q.id}`))
    } catch (e: any) {
      setOpen(null)
      setError(e?.message || 'Could not open that request')
    }
  }

  const close = () => {
    setOpen(null)
    setDetail(null)
  }

  const record = async () => {
    if (!user || !open || !detail || !outcome || !note.trim()) return
    setBusy(true)
    setFormError('')
    setNotice('')
    try {
      await apiJson(user, `/api/support/rule-requests/${open.tenant_id}/${open.id}/responses`, {
        method: 'POST',
        body: { outcome, note: note.trim(), expected_seq: detail.request.responses.length },
      })
      setNotice(`Recorded as ${outcome === 'done' ? 'Done' : 'Declined'}. No rule was changed by recording it.`)
      setOutcome('')
      setNote('')
      setDetail(await apiJson<QueueDetail>(user, `/api/support/rule-requests/${open.tenant_id}/${open.id}`))
      loadQueue()
    } catch (e: any) {
      const b = e instanceof ApiError ? e.body : null
      if (b?.code === 'STALE' && b.current) {
        // Show what got there first; the draft note stays so it can be kept, edited or dropped.
        setDetail((d) => (d ? { ...d, request: { ...d.request, ...b.current } } : d))
        setFormError('Someone else answered this request since you opened it. Their answer is shown above. Read it before recording yours; your note is kept.')
        loadQueue()
      } else {
        setFormError(e?.message || 'The answer was not recorded')
      }
    } finally {
      setBusy(false)
    }
  }

  const FILTERS: ['pending' | 'answered' | 'all', string][] = [
    ['pending', 'Waiting'],
    ['answered', 'Answered'],
    ['all', 'All'],
  ]

  return (
    <div className={styles.client}>
      <div className={styles.clientBanner}>
        <span className={styles.clientDot} aria-hidden="true" />
        <div>
          <div className={styles.clientBannerTitle}>Dataphyll only</div>
          <div className={styles.clientBannerText}>
            Rule changes workspaces have asked for, from every subscription you support. Answering records a decision and tells the workspace. It does not change a
            processing rule, replay events or recalculate wages: a rule changes only when Dataphyll changes it separately.
          </div>
        </div>
      </div>

      <div className={styles.queueFilter} role="group" aria-label="Show">
        {FILTERS.map(([k, label]) => (
          <button key={k} type="button" className={filter === k ? pj.save : pj.cancelBtn} aria-pressed={filter === k} onClick={() => setFilter(k)}>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className={styles.error} role="alert">
          <p>{error}</p>
        </div>
      )}
      {!items && !error && <div className={styles.loading}>Loading rule requests...</div>}
      {items && items.length === 0 && (
        <p className={styles.ruleWhy} data-testid="rule-queue-empty">
          {filter === 'pending' ? 'No request is waiting for an answer.' : 'Nothing here.'}
        </p>
      )}
      {items && items.length > 0 && (
        <div className={pa.card} aria-label="Rule requests">
          <div style={{ padding: '0.25rem 1.125rem' }}>
            {items.map((q) => (
              <div key={`${q.tenant_id}/${q.id}`} className={styles.queueRow} data-testid="rule-queue-row">
                <b>{q.tenant_name}</b>
                <span>
                  {q.rule_keys.map(ruleName).join(', ')}: {q.requested_change}
                </span>
                <span className={styles.ruleWhy}>
                  {q.requested_by} · {fmtStamp(q.requested_at)}
                </span>
                <span className={`${styles.badge} ${statusChip(q.status)}`}>{STATUS_TEXT[q.status]}</span>
                <button type="button" className={pj.archiveBtn} onClick={() => view(q)}>
                  Open
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {open && (
        <section className={styles.rulesPanel} aria-label="Rule request" data-testid="rule-queue-detail">
          {!detail ? (
            <div className={styles.loading}>Loading the request...</div>
          ) : (
            <>
              <h2>
                {detail.request.tenant_name}: {detail.request.rule_keys.map(ruleName).join(', ')}
              </h2>
              <p>{detail.request.requested_change}</p>
              {detail.request.reason && <p className={styles.ruleWhy}>Why: {detail.request.reason}</p>}
              <p className={styles.ruleWhy}>
                Asked by {detail.request.requested_by} · {fmtStamp(detail.request.requested_at)}
              </p>

              <div className={styles.rulesCompare} role="table" aria-label="Rules when sent and now">
                <span className={styles.rulesCompareHead}>Rule</span>
                <span className={styles.rulesCompareHead}>When it was sent</span>
                <span className={styles.rulesCompareHead}>In force now</span>
                {RULES.map((r) => {
                  const then = detail.rules_when_sent ? ruleValue(detail.rules_when_sent, r.key) : 'Not recorded'
                  const now = ruleValue(detail.rules_now, r.key)
                  return (
                    <React.Fragment key={r.name}>
                      <span>{r.name}</span>
                      <span>{then}</span>
                      <span className={then !== now ? styles.rulesChanged : undefined}>{now}</span>
                    </React.Fragment>
                  )
                })}
              </div>

              <h2>Answers</h2>
              {detail.request.responses.length === 0 && <p className={styles.ruleWhy}>Not answered yet.</p>}
              {detail.request.responses.map((a) => (
                <div key={a.seq} className={styles.answer} data-testid="rule-queue-answer">
                  <span>
                    <b>{a.outcome === 'done' ? 'Done' : 'Declined'}</b>
                  </span>
                  <span>{a.note}</span>
                  <span className={styles.ruleWhy}>
                    {a.responded_by} · {fmtStamp(a.responded_at)}
                  </span>
                </div>
              ))}

              <h2>{detail.request.responses.length ? 'Replace the answer' : 'Record an answer'}</h2>
              <div className={styles.queueFilter} role="radiogroup" aria-label="Outcome">
                {(['done', 'declined'] as const).map((o) => (
                  <label key={o} className={styles.clientCheck}>
                    <input type="radio" name="rule-outcome" checked={outcome === o} disabled={busy} onChange={() => setOutcome(o)} />
                    {o === 'done' ? 'Done' : 'Declined'}
                  </label>
                ))}
              </div>
              <label className={pa.formFieldWide}>
                <span className={pa.label}>Note for the workspace. Required.</span>
                <textarea
                  className={pa.pillInput}
                  rows={3}
                  maxLength={2000}
                  value={note}
                  disabled={busy}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What was decided, and why. The workspace reads this."
                />
              </label>
              <p className={styles.ruleWhy}>
                Recording this changes no rule and recalculates nothing. Say in the note when the change will be made, if it is Done.
              </p>
              {formError && (
                <p className={styles.serverError} role="alert">
                  {formError}
                </p>
              )}
              {notice && (
                <p className={styles.rulesSent} role="status">
                  {notice}
                </p>
              )}
              <div className={styles.queueFilter}>
                <PendingButton busy={busy} pending="Recording…" className={pa.primaryBtn} disabled={!outcome || !note.trim()} onClick={record}>
                  Record answer
                </PendingButton>
                <button type="button" className={pj.cancelBtn} onClick={close} disabled={busy}>
                  Close
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  )
}

// ── Client Setup (§7n.16; §9, "Time zone: two zones, one clock for money") ─

interface TenantSettings {
  id: string
  name: string
  timezone: string | null
  zone: ZoneNow | null
  timezone_locked: boolean
  locked_by: string[]
  region?: Region
  regions?: Region[]
  // §9.4c: the season Reports use, and the Strike Board periods kept.
  season?: { start: string | null; source: 'dataphyll' | 'inferred'; override: string | null; inferred: string | null }
  strike_periods?: string[]
  strike_period_options?: { k: string; label: string }[]
  // D2: the one product, and the Stock & Dispatch licence (PackRight only).
  product?: 'grow' | 'packright' | null
  product_label?: string | null
  products?: { k: 'grow' | 'packright'; label: string }[]
  modules?: { stock_dispatch: { state: 'licensed' | 'not_licensed' | 'not_available' | 'unconfigured'; licence: 'paid' | 'included' | null } }
  licences?: ('paid' | 'included')[]
  // A Crew session with no clock-off ends at the worker's last pick, not the
  // team's clock-off time (migration 152).
  last_pick_is_clock_off?: boolean
  can_edit: boolean
  // D9.3: an ADMIN chooses the Strike Board periods too (in Reports).
  can_edit_strike_periods?: boolean
}

interface Region {
  code: string
  label: string
}
interface BreakRule {
  kind: 'paid' | 'unpaid'
  mins: number
  afterH: number
  over: boolean
}
// GET /api/settings/tenant/region-change: the warning before a switch.
interface RegionChange {
  from: Region
  to: Region & { rules: BreakRule[]; basis_note: string }
  unchanged: boolean
  versions_retired: number
  queued: number
  locked_dates: string[]
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
const ruleText = (r: BreakRule) => `${r.mins} min ${r.kind} once the day ${r.over ? 'passes' : 'reaches'} ${r.afterH} h`

// Each field with its line saying what it is for (§7n.16). The four Control
// does not record yet say so rather than show a made-up value.
const NOT_RECORDED: [string, string][] = [
  ['ClientId (legacy)', "The previous system's short client key, a name such as demosite, not a number. Kept for reconciling historical payroll exports and support tickets."],
  ['Packhouse ID (legacy)', "The previous system's numeric packhouse identifier, e.g. 46001, which appears in historical packhouse records and on old labels."],
  ['Trading name', 'What the app and the workspace picker show.'],
  ['Tenant address', 'Registered address for documents and establishment paperwork.'],
]

function ClientSetupTab() {
  const { user } = useAuth()
  const { currentWorkspace, noteTimezone, refreshAccess } = useWorkspace()
  const [data, setData] = useState<TenantSettings | null>(null)
  const [choice, setChoice] = useState('')
  // Which write is out (h 3975-4002): every save here waits on it, and only
  // the button pressed says its verb once the wait is real.
  const [busyKind, setBusyKind] = useState<'tz' | 'review' | 'region' | 'season' | 'seasonClear' | 'strike' | 'product' | 'licence' | 'clockOff' | null>(null)
  const busy = busyKind !== null
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const [regionChoice, setRegionChoice] = useState('')
  const [change, setChange] = useState<RegionChange | null>(null)
  const [seasonChoice, setSeasonChoice] = useState('')
  const [strikeChoice, setStrikeChoice] = useState<string[]>([])
  const [productChoice, setProductChoice] = useState('')
  const [licenceChoice, setLicenceChoice] = useState('')
  const [clockOffChoice, setClockOffChoice] = useState('team')
  const uid = user?.uid

  const show = (t: TenantSettings) => {
    setData(t)
    setChoice(t.timezone || '')
    setRegionChoice(t.region?.code || '')
    setChange(null)
    setSeasonChoice(t.season?.override || '')
    setStrikeChoice(t.strike_periods || [])
    setProductChoice(t.product || '')
    setLicenceChoice(t.modules?.stock_dispatch.licence || '')
    setClockOffChoice(t.last_pick_is_clock_off ? 'pick' : 'team')
  }

  // A load the effect has since re-run past is dropped: arriving late, it
  // would reset a choice or a warning already made on the newer one.
  useEffect(() => {
    if (!user) return
    let live = true
    apiJson<TenantSettings>(user, '/api/settings/tenant')
      .then((t) => live && show(t))
      .catch((e: any) => live && setError(e?.message || 'Could not load the client setup'))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3600)
    return () => clearTimeout(t)
  }, [toast])

  // The region warning is a layer (h 425-458): Escape is its Keep, and puts the
  // choice back to the region in force. Nothing closes while the switch runs.
  useLayer(
    !!change,
    () => {
      if (busy || !change) return
      setChange(null)
      setRegionChoice(change.from.code)
    },
    { busy }
  )

  if (!data)
    return error ? (
      <div className={styles.error} role="alert">
        <p>{error}</p>
      </div>
    ) : (
      <div className={styles.loading}>Loading client setup...</div>
    )

  // Work already read on the tenant's clock fixes it (SITE-1). While it is
  // unset that clock is UTC, so UTC is the one zone still open to it.
  const zones = data.timezone_locked ? [data.timezone || 'UTC'] : zoneOptions(data.timezone)
  const canSave = data.can_edit && !!choice && choice !== data.timezone && !busy

  const save = async () => {
    if (!canSave) return
    setBusyKind('tz')
    setError('')
    try {
      const t = await apiJson<TenantSettings>(user, '/api/settings/tenant/timezone', { method: 'PUT', body: { timezone: choice, before: data.timezone } })
      show(t)
      // Today moves to the new clock now, not at the next sign-in, and stays
      // on it after a switch away and back.
      if (currentWorkspace) noteTimezone(currentWorkspace.id, t.timezone)
      setToast(`Operating time zone set to ${t.timezone}.`)
    } catch (e: any) {
      const body = e instanceof ApiError ? e.body : null
      if (body?.code === 'STALE' && body.tenant) {
        show(body.tenant)
        setError('Someone else changed the operating time zone since you opened it. This shows what is saved now.')
      } else if (body?.code === 'TIMEZONE_LOCKED') {
        setData({ ...data, timezone_locked: true, locked_by: body.locked_by || [] })
        setChoice(data.timezone || '')
        setError(e.message)
      } else {
        setError(e?.message || 'The time zone was not saved')
      }
    } finally {
      setBusyKind(null)
    }
  }

  // A region change restores the new region's shipped break rules for every
  // day and replays the tenant (§7j.3c), so the warning is read from the
  // server before the switch is offered.
  const regions = data.regions || []
  const canReview = data.can_edit && !!data.region && !!regionChoice && regionChoice !== data.region.code && !busy
  const review = async () => {
    if (!canReview) return
    setBusyKind('review')
    setError('')
    try {
      setChange(await apiJson<RegionChange>(user, `/api/settings/tenant/region-change?region=${encodeURIComponent(regionChoice)}`))
    } catch (e: any) {
      setError(e?.message || 'Could not work out what the change would do')
    } finally {
      setBusyKind(null)
    }
  }
  const switchRegion = async () => {
    if (!change || !data.region || busy) return
    setBusyKind('region')
    setError('')
    try {
      const t = await apiJson<TenantSettings & { change: { queued: number; locked_dates: string[] } }>(user, '/api/settings/tenant/region', {
        method: 'PUT',
        body: { region: change.to.code, before: data.region.code },
      })
      show(t)
      const locked = t.change.locked_dates.length ? `; locked days not recalculated: ${t.change.locked_dates.join(', ')}` : ''
      setToast(`Region set to ${t.region?.label}. ${plural(t.change.queued, 'worker/day', 'worker/days')} queued for recalculation${locked}.`)
    } catch (e: any) {
      const body = e instanceof ApiError ? e.body : null
      if (body?.code === 'STALE' && body.tenant) {
        show(body.tenant)
        setError('Someone else changed the region since you opened it. This shows what is saved now.')
      } else {
        setError(e?.message || 'The region was not changed')
      }
    } finally {
      setBusyKind(null)
    }
  }

  // The two Reports settings (§9.4c) save alone, each against the value the
  // form opened with. Reports read them when opened: nothing is recalculated.
  const putSetting = async (kind: 'season' | 'seasonClear' | 'strike' | 'product' | 'licence' | 'clockOff', path: string, body: object, what: string, done: (t: TenantSettings) => string) => {
    setBusyKind(kind)
    setError('')
    try {
      const t = await apiJson<TenantSettings>(user, path, { method: 'PUT', body })
      show(t)
      setToast(done(t))
    } catch (e: any) {
      const b = e instanceof ApiError ? e.body : null
      if (b?.code === 'STALE' && b.tenant) {
        show(b.tenant)
        setError(`Someone else changed the ${what} since you opened it. This shows what is saved now.`)
      } else {
        setError(e?.message || `The ${what} was not saved`)
      }
    } finally {
      setBusyKind(null)
    }
  }
  const season = data.season
  const seasonOverride = season?.override || ''
  const canSaveSeason = data.can_edit && !!season && !!seasonChoice && seasonChoice !== seasonOverride && !busy
  const saveSeason = (next: string | null) =>
    putSetting(next ? 'season' : 'seasonClear', '/api/settings/tenant/season', { season_start: next, before: season?.override ?? null }, 'season start', (t) =>
      t.season?.override
        ? `Season start set to ${dmy(t.season.override)}.`
        : `Season start cleared: ${t.season?.inferred ? `inferred from the first picking day, ${dmy(t.season.inferred)}` : 'it will be the first picking day'}.`
    )
  const strikeOptions = data.strike_period_options || []
  const strikeSaved = data.strike_periods || []
  const strikeNext = strikeOptions.map((o) => o.k).filter((k) => strikeChoice.includes(k))
  const canSaveStrike = data.can_edit && strikeNext.length > 0 && strikeNext.join() !== strikeSaved.join() && !busy
  const saveStrike = () =>
    canSaveStrike &&
    putSetting('strike', '/api/settings/tenant/strike-periods', { periods: strikeNext, before: strikeSaved }, 'Strike Board periods', (t) =>
      `The Strike Board offers ${strikeOptions.filter((o) => t.strike_periods?.includes(o.k)).map((o) => o.label).join(' and ')}.`
    )
  const labelOf = (k: string) => strikeOptions.find((o) => o.k === k)?.label || k

  // D2: the product and the licence, each against the value the form opened
  // with. The server refuses what cannot change (a load still open, Stock &
  // Dispatch records with no designed path to another product) and says why;
  // the refusal is shown as it came. /api/me is re-read after a change, so
  // the rail follows it at once.
  const products = data.products || []
  const productSaved = data.product ?? null
  const canSaveProduct = data.can_edit && !!productChoice && productChoice !== productSaved && !busy
  const saveProduct = () =>
    canSaveProduct &&
    putSetting('product', '/api/settings/tenant/product', { product: productChoice, before: productSaved }, 'product', (t) => {
      refreshAccess()
      return `Product set to ${t.product_label}.`
    })
  const licenceSaved = data.modules?.stock_dispatch.licence ?? null
  const licenceNext = licenceChoice || null
  const canSaveLicence = data.can_edit && data.product === 'packright' && licenceNext !== licenceSaved && !busy
  const saveLicence = () =>
    canSaveLicence &&
    putSetting('licence', '/api/settings/tenant/licence', { licence: licenceNext, before: licenceSaved }, 'Stock & Dispatch licence', (t) => {
      refreshAccess()
      const l = t.modules?.stock_dispatch.licence
      return l ? `Stock & Dispatch licensed (${LICENCE_TEXT[l] || l}).` : 'Stock & Dispatch licence removed. Its records and grants are kept.'
    })

  const clockOffSaved = data.last_pick_is_clock_off === true
  const canSaveClockOff = data.can_edit && (clockOffChoice === 'pick') !== clockOffSaved && !busy
  const saveClockOff = () =>
    canSaveClockOff &&
    putSetting('clockOff', '/api/settings/tenant/clock-off', { last_pick_is_clock_off: clockOffChoice === 'pick', before: clockOffSaved }, 'Crew clock-off setting', (t) =>
      t.last_pick_is_clock_off
        ? 'A Crew shift now ends at the worker’s last pick, preassign or receipt.'
        : 'A Crew shift now ends at its clock-off, or the team’s clock-off time when none comes.'
    )

  return (
    <div className={styles.client}>
      <div className={styles.clientBanner}>
        <span className={styles.clientDot} aria-hidden="true" />
        <div>
          <div className={styles.clientBannerTitle}>Dataphyll only</div>
          <div className={styles.clientBannerText}>
            Identity and operating details for this tenant. Dataphyll-only: a client admin cannot see or change them, because they are how every record,
            integration and invoice is keyed.
          </div>
        </div>
      </div>

      <div className={styles.clientFields} role="list" aria-label="Client Setup">
        <div className={styles.clientField} role="listitem">
          <span className={styles.clientKey}>TenantId</span>
          <span className={styles.clientMono}>{data.id}</span>
          <span className={styles.clientNote}>System identity. Every record, label QR and API call is keyed on it. Never reissued, never reused.</span>
        </div>
        <div className={styles.clientField} role="listitem">
          <span className={styles.clientKey}>Tenant name</span>
          <span className={styles.clientValue}>{data.name}</span>
          <span className={styles.clientNote}>
            Control holds one name for a tenant, the one the workspace picker shows. A separate legal and trading name is not recorded yet.
          </span>
        </div>
        <div className={styles.clientField} role="listitem">
          <label className={styles.clientKey} htmlFor="cs-product">
            Product
          </label>
          {data.can_edit ? (
            <div className={styles.clientZone}>
              <select id="cs-product" className={pj.input} value={productChoice} disabled={busy} onChange={(e) => setProductChoice(e.target.value)}>
                {!productSaved && (
                  <option value="" disabled>
                    Not set: choose a product…
                  </option>
                )}
                {products.map((p) => (
                  <option key={p.k} value={p.k}>
                    {p.label}
                  </option>
                ))}
              </select>
              <PendingButton className={canSaveProduct ? pj.save : pj.saveBlocked} disabled={!canSaveProduct} busy={busyKind === 'product'} pending="Saving…" onClick={saveProduct}>
                Save product
              </PendingButton>
            </div>
          ) : (
            <span className={productSaved ? styles.clientValue : styles.clientNone}>{data.product_label || 'Not set'}</span>
          )}
          <span className={styles.clientNote} data-testid="cs-product-note">
            The one product this tenant has bought. It decides which screens exist: PackRight has packing lists and is eligible for Stock & Dispatch; Grow has
            neither. {productSaved ? '' : 'Not set yet: nothing product-specific is shown until it is. '}Changing it is audited, and is refused while Stock &
            Dispatch records exist, because they have no designed path to Grow.
          </span>
        </div>
        {data.product === 'packright' && (
          <div className={styles.clientField} role="listitem">
            <label className={styles.clientKey} htmlFor="cs-licence">
              Stock & Dispatch licence
            </label>
            {data.can_edit ? (
              <div className={styles.clientZone}>
                <select id="cs-licence" className={pj.input} value={licenceChoice} disabled={busy} onChange={(e) => setLicenceChoice(e.target.value)}>
                  <option value="">Not licensed</option>
                  {(data.licences || []).map((l) => (
                    <option key={l} value={l}>
                      {l === 'paid' ? 'Licensed, paid' : 'Licensed, included'}
                    </option>
                  ))}
                </select>
                <PendingButton className={canSaveLicence ? pj.save : pj.saveBlocked} disabled={!canSaveLicence} busy={busyKind === 'licence'} pending="Saving…" onClick={saveLicence}>
                  Save licence
                </PendingButton>
              </div>
            ) : (
              <span className={styles.clientValue}>{licenceSaved ? `Licensed, ${licenceSaved}` : 'Not licensed'}</span>
            )}
            <span className={styles.clientNote} data-testid="cs-licence-note">
              The one separately licensed module. A licence grants no capability: people still need their own Stock & Dispatch grants in Personnel.
              Removing it keeps every record, history and grant, and is refused while a load is still open. Changing it is audited.
            </span>
          </div>
        )}
        <div className={styles.clientField} role="listitem">
          <label className={styles.clientKey} htmlFor="cs-clock-off">
            Crew shift ends at
          </label>
          {data.can_edit ? (
            <div className={styles.clientZone}>
              <select id="cs-clock-off" className={pj.input} value={clockOffChoice} disabled={busy} onChange={(e) => setClockOffChoice(e.target.value)}>
                <option value="team">Its clock-off, or the team’s clock-off time</option>
                <option value="pick">The worker’s last pick, preassign or receipt</option>
              </select>
              <PendingButton className={canSaveClockOff ? pj.save : pj.saveBlocked} disabled={!canSaveClockOff} busy={busyKind === 'clockOff'} pending="Saving…" onClick={saveClockOff}>
                Save clock-off
              </PendingButton>
            </div>
          ) : (
            <span className={styles.clientValue}>{clockOffSaved ? 'The worker’s last pick, preassign or receipt' : 'Its clock-off, or the team’s clock-off time'}</span>
          )}
          <span className={styles.clientNote} data-testid="cs-clock-off-note">
            When no clock-off comes from Crew, Control finishes the shift at the team’s clock-off time, or at a later pick if they were still working;
            picks that upload later move it later, up to their next clock-on or a day after they started. Last pick: every Crew shift ends at the last
            pick, preassign or receipt credited to the worker (a tagless handover or worker card too), even before the team’s time, and it beats the
            worker’s or a supervisor’s own clock-off; it still waits for the team’s time when no clock-off comes. A finish someone edits on Timesheets
            stays as they set it. Changing it is audited and leaves finishes already made as they are.
          </span>
        </div>
        {NOT_RECORDED.map(([k, note]) => (
          <div key={k} className={styles.clientField} role="listitem">
            <span className={styles.clientKey}>{k}</span>
            <span className={styles.clientNone}>Not recorded in Control yet</span>
            <span className={styles.clientNote}>{note}</span>
          </div>
        ))}
        <div className={styles.clientField} role="listitem">
          <label className={styles.clientKey} htmlFor="cs-tz">
            Operating time zone
          </label>
          {data.can_edit ? (
            <div className={styles.clientZone}>
              <select id="cs-tz" className={pj.input} value={choice} disabled={zones.length < 2 && choice === zones[0]} onChange={(e) => setChoice(e.target.value)}>
                {!data.timezone && (
                  <option value="" disabled>
                    Not set: choose a zone…
                  </option>
                )}
                {zones.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
              <PendingButton className={canSave ? pj.save : pj.saveBlocked} disabled={!canSave} busy={busyKind === 'tz'} pending="Saving…" onClick={save}>
                Save time zone
              </PendingButton>
            </div>
          ) : (
            <span className={data.timezone ? styles.clientMono : styles.clientNone}>{data.timezone || 'Not set'}</span>
          )}
          <span className={styles.clientNote}>
            The business clock. Pay periods, day states, report periods and "today" all run on it, for everyone, whatever zone they are sitting in. An IANA zone
            name, never an offset: an offset is wrong twice a year. A site in another zone can override how its own captures are read (Sites & jobs → site
            settings), but never this.
          </span>
          {data.timezone_locked && (
            <span className={styles.clientNote} data-testid="cs-locked">
              Work is already recorded on this clock{data.locked_by.length ? ` at ${data.locked_by.join(', ')}` : ''}, so it is fixed: what a change would do to
              the days already recorded is not settled yet (SITE-1).
            </span>
          )}
          {!data.timezone && (
            <span className={styles.clientNote} data-testid="cs-unset">
              Not set yet. Until it is, a new site must be given its own zone, and any record with no site of its own is read as UTC, as it was before tenant
              zones existed.
            </span>
          )}
        </div>
        <div className={styles.clientField} role="listitem">
          <span className={styles.clientKey}>Current offset</span>
          {data.zone ? <span className={styles.clientValue}>{offsetText(data.zone)}</span> : <span className={styles.clientNone}>No zone set</span>}
          <span className={styles.clientNote}>Derived from the zone at read time, never stored. Shown so support can tell a real delay from a time-zone difference.</span>
        </div>
        {data.region && (
          <div className={styles.clientField} role="listitem">
            <label className={styles.clientKey} htmlFor="cs-region">
              Break rule region
            </label>
            {data.can_edit ? (
              <div className={styles.clientZone}>
                <select
                  id="cs-region"
                  className={pj.input}
                  value={regionChoice}
                  disabled={regions.length < 2 || !!change}
                  onChange={(e) => setRegionChoice(e.target.value)}
                >
                  {regions.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </select>
                {!change && (
                  <PendingButton className={canReview ? pj.save : pj.saveBlocked} disabled={!canReview} busy={busyKind === 'review'} pending="Working it out…" onClick={review}>
                    Review the change
                  </PendingButton>
                )}
              </div>
            ) : (
              <span className={styles.clientValue}>{data.region.label}</span>
            )}
            <span className={styles.clientNote}>
              Picks the shipped break rules the break schedule opens on (Timesheets → Break schedule). Each region has its own labels, note and thresholds; only
              regions with a shipped rule set are offered.
              {regions.length < 2 && ` Only ${data.region.label} has one so far.`}
            </span>
            {change && (
              <div className={styles.regionWarning} role="group" aria-label="Region change" data-testid="cs-region-warning">
                <p>
                  <strong>
                    Switching from {change.from.label} to {change.to.label} restores {change.to.label}’s shipped break rules for every day, past days
                    included.
                  </strong>
                </p>
                <ul>
                  {change.to.rules.length ? change.to.rules.map((r, i) => <li key={i}>{ruleText(r)}</li>) : <li>No breaks at all.</li>}
                </ul>
                <p>
                  {change.versions_retired
                    ? `The ${plural(change.versions_retired, 'break schedule version', 'break schedule versions')} this tenant set ${change.versions_retired === 1 ? 'is' : 'are'} retired: kept on record, and not restored by switching back.`
                    : 'This tenant has not set its own break schedule, so nothing of its own is retired.'}{' '}
                  {plural(change.queued, 'worker/day is', 'worker/days are')} recalculated.
                  {change.locked_dates.length > 0 &&
                    ` Locked days keep the wages they were locked with and are not recalculated: ${change.locked_dates.join(', ')}.`}
                </p>
                <p>{change.to.basis_note}</p>
                <div className={styles.clientZone}>
                  <button
                    type="button"
                    className={pj.cancelBtn}
                    disabled={busy}
                    onClick={() => {
                      setChange(null)
                      setRegionChoice(change.from.code)
                    }}
                  >
                    Keep {change.from.label}
                  </button>
                  <PendingButton className={pj.save} disabled={busy} busy={busyKind === 'region'} pending="Switching…" onClick={switchRegion}>
                    Switch to {change.to.label}
                  </PendingButton>
                </div>
              </div>
            )}
          </div>
        )}
        {season && (
          <div className={styles.clientField} role="listitem">
            <label className={styles.clientKey} htmlFor="cs-season">
              Season start
            </label>
            {data.can_edit ? (
              <div className={styles.clientZone}>
                <DateField id="cs-season" className={pj.input} value={seasonChoice} onChange={(e) => setSeasonChoice(e.target.value)} />
                <PendingButton
                  className={canSaveSeason ? pj.save : pj.saveBlocked}
                  disabled={!canSaveSeason}
                  busy={busyKind === 'season'}
                  pending="Saving…"
                  onClick={() => canSaveSeason && saveSeason(seasonChoice)}
                >
                  Save season start
                </PendingButton>
                {season.override && (
                  <PendingButton className={pj.cancelBtn} disabled={busy} busy={busyKind === 'seasonClear'} pending="Clearing…" onClick={() => saveSeason(null)}>
                    Use the first picking day
                  </PendingButton>
                )}
              </div>
            ) : (
              <span className={season.start ? styles.clientValue : styles.clientNone}>{season.start ? dmy(season.start) : 'No picking yet'}</span>
            )}
            <span className={styles.clientNote} data-testid="cs-season-note">
              {season.override
                ? `Set by Dataphyll to ${dmy(season.override)}. `
                : season.inferred
                  ? `Not set: inferred from the first picking day, ${dmy(season.inferred)}. `
                  : 'Not set, and nothing has been picked yet: the season will start on the first picking day. '}
              Reports' Whole season, the Season report and the Leaderboard's Season period start here. The Dashboard's weekly chart keeps its own week
              39 to week 12 window.
              {season.override && season.inferred && ` The first picking day is ${dmy(season.inferred)}.`}
            </span>
          </div>
        )}
        {strikeOptions.length > 0 && (
          <div className={styles.clientField} role="listitem">
            <span className={styles.clientKey} id="cs-strike">
              Strike Board periods
            </span>
            {data.can_edit ? (
              <div className={styles.clientZone} role="group" aria-labelledby="cs-strike">
                {strikeOptions.map((o) => (
                  <label key={o.k} className={styles.clientCheck}>
                    <input
                      type="checkbox"
                      checked={strikeChoice.includes(o.k)}
                      // At least one stays: a board with no period shows nothing.
                      disabled={busy || (strikeChoice.includes(o.k) && strikeChoice.length === 1)}
                      onChange={(e) => setStrikeChoice((c) => (e.target.checked ? [...c, o.k] : c.filter((k) => k !== o.k)))}
                    />
                    {o.label}
                  </label>
                ))}
                <PendingButton className={canSaveStrike ? pj.save : pj.saveBlocked} disabled={!canSaveStrike} busy={busyKind === 'strike'} pending="Saving…" onClick={saveStrike}>
                  Save periods
                </PendingButton>
              </div>
            ) : (
              <span className={styles.clientValue}>{strikeSaved.map(labelOf).join(', ')}</span>
            )}
            <span className={styles.clientNote}>
              The periods the Strike Board offers; the first kept is the one it opens on. Today and Past week only: a longer window stops describing the
              crew working now.
            </span>
          </div>
        )}
        {data.can_edit && <LargeBatchThreshold />}
      </div>

      {error && (
        <div className={styles.error} role="alert">
          <p>{error}</p>
        </div>
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}
